/**
 * Procedural music engine — a small EDM / military-industrial playlist.
 *
 * Each "song" is a tempo, key and chord progression plus a style that drives
 * a 16-step drum machine, a bassline and brass/synth stabs. The scheduler
 * queues notes ahead of time on the WebAudio clock so the groove stays tight.
 * Songs advance automatically like a playlist.
 *
 * The ambient playlist is synthesised. The authored battle-opening cue is
 * loaded separately so it can be previewed from the command menu and played
 * when a sortie begins.
 */

import { saveManager } from './SaveManager';

type SongStyle = 'march' | 'edm' | 'dark' | 'rush';

interface SongDef {
  name: string;
  bpm: number;
  /** MIDI note of the tonic. */
  root: number;
  /** Song length in bars. */
  bars: number;
  style: SongStyle;
  /** One chord per bar, as semitone offsets from the tonic. */
  prog: number[][];
}

interface AuthoredTrackDef {
  name: string;
  path: string;
}

type PlaylistEntry =
  | { kind: 'synth'; index: number }
  | { kind: 'audio'; index: number };

const CH = {
  i: [0, 3, 7],
  iv: [5, 8, 12],
  VI: [8, 12, 15],
  III: [3, 7, 10],
  VII: [10, 14, 17],
  V: [7, 11, 14],
  bII: [1, 5, 8],
};

const SONGS: SongDef[] = [
  { name: 'IRON MARCH', bpm: 112, root: 45, bars: 16, style: 'march', prog: [CH.i, CH.VI, CH.III, CH.VII] },
  { name: 'NEON SIEGE', bpm: 128, root: 43, bars: 16, style: 'edm', prog: [CH.i, CH.iv, CH.VI, CH.V] },
  { name: 'COLD FRONT', bpm: 100, root: 41, bars: 16, style: 'dark', prog: [CH.i, CH.bII, CH.VI, CH.V] },
  { name: 'OVERDRIVE', bpm: 140, root: 45, bars: 16, style: 'rush', prog: [CH.i, CH.VII, CH.VI, CH.V] },
  { name: 'STEEL RAIN', bpm: 124, root: 44, bars: 16, style: 'edm', prog: [CH.i, CH.VI, CH.iv, CH.V] },
  { name: 'GHOST SIGNAL', bpm: 96, root: 40, bars: 16, style: 'dark', prog: [CH.i, CH.III, CH.VI, CH.VII] },
  { name: 'BLITZ PROTOCOL', bpm: 150, root: 46, bars: 16, style: 'rush', prog: [CH.i, CH.V, CH.VI, CH.VII] },
  { name: 'HOLD THE LINE', bpm: 118, root: 43, bars: 16, style: 'march', prog: [CH.i, CH.VII, CH.VI, CH.V] },
  { name: 'AFTERBURN', bpm: 132, root: 45, bars: 16, style: 'edm', prog: [CH.i, CH.iv, CH.VII, CH.V] },
  { name: 'VANGUARD', bpm: 116, root: 44, bars: 16, style: 'march', prog: [CH.i, CH.VII, CH.VI, CH.III] },
  { name: 'HYPERDRIVE', bpm: 130, root: 42, bars: 16, style: 'edm', prog: [CH.i, CH.VI, CH.VII, CH.V] },
  // Additional generated MIDI-style cuts: the faster rush/EDM pieces bring
  // drive, while the lower-BPM dark and march pieces leave room to breathe.
  { name: 'CIRCUIT BLOOM', bpm: 122, root: 42, bars: 16, style: 'edm', prog: [CH.i, CH.VI, CH.III, CH.VII] },
  { name: 'TIDAL SYNTHESIS', bpm: 108, root: 39, bars: 16, style: 'dark', prog: [CH.i, CH.iv, CH.bII, CH.V] },
  { name: 'LASER RAIN', bpm: 136, root: 47, bars: 16, style: 'rush', prog: [CH.i, CH.V, CH.VI, CH.iv] },
  { name: 'AURORA CIRCUIT', bpm: 104, root: 44, bars: 16, style: 'dark', prog: [CH.i, CH.VI, CH.III, CH.V] },
  { name: 'PULSE VECTOR', bpm: 126, root: 46, bars: 16, style: 'edm', prog: [CH.i, CH.VII, CH.iv, CH.V] },
  { name: 'NIGHT DRIVE', bpm: 110, root: 41, bars: 16, style: 'march', prog: [CH.i, CH.III, CH.VI, CH.VII] },
  { name: 'STATIC BLOOM', bpm: 132, root: 43, bars: 16, style: 'edm', prog: [CH.i, CH.iv, CH.III, CH.V] },
  { name: 'GRAVITY WELL', bpm: 98, root: 39, bars: 16, style: 'dark', prog: [CH.i, CH.bII, CH.VII, CH.VI] },
  { name: 'QUANTUM RUN', bpm: 146, root: 48, bars: 16, style: 'rush', prog: [CH.i, CH.VII, CH.VI, CH.V] },
  { name: 'SIGNAL FLARE', bpm: 120, root: 45, bars: 16, style: 'march', prog: [CH.i, CH.VI, CH.V, CH.III] },
];

/** Authored MP3s shared by the menu and battle playlists. */
const PLAYLIST_AUDIO: AuthoredTrackDef[] = [
  { name: 'BATTLE COMMENCES', path: '/assets/audio/battle-commences.mp3' },
  { name: 'TACTICAL COOLDOWN', path: '/assets/audio/Tactical Cooldown.mp3' },
  { name: 'TACTICAL COOLDOWN FUNKY', path: '/assets/audio/Tactical Cooldown funky.mp3' },
  { name: 'TACTICAL ASSAULT', path: '/assets/audio/Tactical Assault.mp3' },
  { name: 'TACTICAL BREAKDOWN', path: '/assets/audio/Tactical Breakdown.mp3' },
];

const DRUMS: Record<SongStyle, { kick: number[]; snare: number[]; hat: number[] }> = {
  march: { kick: [0, 8], snare: [4, 12], hat: [2, 6, 10, 14] },
  edm: { kick: [0, 4, 8, 12], snare: [4, 12], hat: [2, 6, 10, 14] },
  dark: { kick: [0, 8], snare: [4, 12], hat: [6, 14] },
  rush: { kick: [0, 4, 8, 12], snare: [4, 12], hat: [1, 3, 5, 7, 9, 11, 13, 15] },
};

const BASS_STEPS: Record<SongStyle, number[]> = {
  march: [0, 6, 8, 14],
  edm: [0, 2, 4, 6, 8, 10, 12, 14],
  dark: [0, 8, 11],
  rush: [0, 4, 8, 12, 14],
};

const midi = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export class MusicSystem {
  private static readonly PLAYLIST_KEY = 'apogee_hold_authored_playlist';
  private static readonly SHUFFLE_KEY = 'apogee_hold_authored_shuffle';
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private bus: GainNode | null = null;
  private comp: DynamicsCompressorNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private timer: number | null = null;
  private started = false;
  private nextTime = 0;
  private step = 0;
  private menuMode = true;
  private intensity = 0;
  private target = 0;
  private songIndex = 0;
  private externalPlayback = false;
  private authoredAudio: HTMLAudioElement | null = null;
  private battleOpeningActive = false;
  private battleOpeningResumeName = SONGS[0].name;
  private battleCueWasSelected = false;
  private authoredShuffle = true;
  /** Mode-specific shuffle bag: MIDI ambience in menus, authored MP3s in battle. */
  private playlistBag: PlaylistEntry[] = [];
  private currentPlaylistEntry: PlaylistEntry = { kind: 'synth', index: 0 };
  enabled = true;
  volume = 1;
  currentSongName = SONGS[0].name;

  constructor() {
    try {
      const saved = JSON.parse(localStorage.getItem(MusicSystem.PLAYLIST_KEY) ?? 'null') as string[] | null;
      if (Array.isArray(saved)) {
        const byName = new Map(PLAYLIST_AUDIO.map((track) => [track.name, track]));
        const ordered = saved.map((name) => byName.get(name)).filter((track): track is AuthoredTrackDef => !!track);
        const remaining = PLAYLIST_AUDIO.filter((track) => !saved.includes(track.name));
        PLAYLIST_AUDIO.splice(0, PLAYLIST_AUDIO.length, ...ordered, ...remaining);
      }
      const shuffle = localStorage.getItem(MusicSystem.SHUFFLE_KEY);
      if (shuffle !== null) this.authoredShuffle = shuffle !== 'false';
    } catch { /* storage unavailable or malformed */ }
  }

  get authoredPlaylist(): string[] {
    return PLAYLIST_AUDIO.map((track) => track.name);
  }

  get isAuthoredShuffle(): boolean { return this.authoredShuffle; }

  toggleAuthoredShuffle(): void {
    this.authoredShuffle = !this.authoredShuffle;
    this.playlistBag = [];
    try { localStorage.setItem(MusicSystem.SHUFFLE_KEY, String(this.authoredShuffle)); } catch { /* storage unavailable */ }
  }

  previewAuthoredTrack(index: number): void {
    if (!PLAYLIST_AUDIO[index]) return;
    this.battleCueWasSelected = true;
    this.playAuthoredTrack(PLAYLIST_AUDIO[index], true);
  }

  removeAuthoredTrack(index: number): void {
    if (PLAYLIST_AUDIO.length <= 1 || !PLAYLIST_AUDIO[index]) return;
    PLAYLIST_AUDIO.splice(index, 1);
    this.playlistBag = [];
    this.persistAuthoredPlaylist();
  }

  moveAuthoredTrack(from: number, to: number): void {
    if (from < 0 || to < 0 || from >= PLAYLIST_AUDIO.length || to >= PLAYLIST_AUDIO.length || from === to) return;
    const [track] = PLAYLIST_AUDIO.splice(from, 1);
    PLAYLIST_AUDIO.splice(to, 0, track);
    this.playlistBag = [];
    this.persistAuthoredPlaylist();
  }

  private persistAuthoredPlaylist(): void {
    try { localStorage.setItem(MusicSystem.PLAYLIST_KEY, JSON.stringify(PLAYLIST_AUDIO.map((track) => track.name))); } catch { /* storage unavailable */ }
  }

  get currentTrackType(): 'SYNTH / MIDI' | 'MP3' {
    return this.authoredAudio ? 'MP3' : 'SYNTH / MIDI';
  }

  private ensure(): AudioContext | null {
    if (!this.enabled) return null;
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Force engines to the persisted music toggle (used on boot and live toggles). */
  applyEnabled(enabled: boolean, volume: number): void {
    this.enabled = enabled;
    this.volume = Math.max(0, Math.min(1, enabled ? volume : 0));
    if (!this.enabled) this.silence();
    else this.applyMaster(0.12);
  }

  /** Stop every music output immediately (procedural + authored HTMLAudio). */
  silence(): void {
    if (this.authoredAudio) {
      try {
        this.authoredAudio.pause();
        this.authoredAudio.currentTime = 0;
      } catch { /* ignore */ }
      this.authoredAudio = null;
    }
    this.battleOpeningActive = false;
    this.externalPlayback = false;
    this.applyMaster(0.05);
    if (this.timer != null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.started = false;
    if (this.ctx && this.ctx.state === 'running') {
      void this.ctx.suspend().catch(() => undefined);
    }
  }

  unlock(): void {
    // Always re-read the persisted toggle — a refresh must honour music OFF.
    try {
      const s = saveManager.data.settings;
      this.enabled = !!s.music;
      this.volume = s.music ? (s.musicVolume ?? 0.8) : 0;
    } catch { /* keep defaults if save is unavailable */ }
    if (!this.enabled) {
      this.silence();
      return;
    }
    const ctx = this.ensure();
    if (!ctx || this.started) return;
    this.started = true;

    // Master chain: bus -> compressor -> master -> out
    this.bus = ctx.createGain();
    this.bus.gain.value = 0.9;
    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -14;
    this.comp.knee.value = 22;
    this.comp.ratio.value = 4;
    this.comp.attack.value = 0.004;
    this.comp.release.value = 0.18;
    this.master = ctx.createGain();
    this.master.gain.value = (this.menuMode ? 0.24 : 0.4) * this.volume;
    this.bus.connect(this.comp).connect(this.master).connect(ctx.destination);

    // Reusable noise buffer for percussion.
    const len = Math.floor(ctx.sampleRate * 0.35);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;

    this.nextTime = ctx.currentTime + 0.1;
    this.timer = window.setInterval(() => this.schedule(), 25);
  }

  /** Menu = MIDI ambience; battle = authored MP3 score at full intensity. */
  setMenuMode(on: boolean): void {
    if (this.menuMode !== on) {
      this.playlistBag = [];
      if (on && this.authoredAudio) {
        this.stopBattleOpening();
        this.battleCueWasSelected = false;
      }
    }
    this.menuMode = on;
    this.applyMaster(0.6);
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, this.enabled ? v : 0));
    if (this.authoredAudio) this.authoredAudio.volume = this.volume * 0.72;
    this.applyMaster(0.15);
  }

  /** Start each map on a stable, varied authored track, then shuffle the rest. */
  playBattleOpening(mapId = 'default'): void {
    const mapSeed = [...mapId].reduce((seed, char) => (seed * 31 + char.charCodeAt(0)) >>> 0, 17);
    const index = mapSeed % PLAYLIST_AUDIO.length;
    this.playlistBag = [];
    this.currentPlaylistEntry = { kind: 'audio', index };
    this.battleCueWasSelected = true;
    this.playAuthoredTrack(PLAYLIST_AUDIO[index], true);
  }

  /** Stop the one-shot opening track when a sortie ends or is abandoned. */
  stopBattleOpening(): void {
    if (!this.authoredAudio || !this.battleOpeningActive) return;
    this.authoredAudio.pause();
    this.authoredAudio.currentTime = 0;
    this.authoredAudio = null;
    this.battleOpeningActive = false;
    this.currentSongName = this.battleOpeningResumeName;
    this.setExternalPlaybackActive(false);
  }

  setIntensity(v: number): void {
    this.target = Math.max(0, Math.min(1, v));
  }

  /** Skip the procedural playlist to the next track immediately. */
  nextSong(): void {
    // Menus stay MIDI-driven. In battle, skipping an authored track advances
    // within the MP3-only playlist so the authored battle cues remain
    // distinct from the procedural command-deck ambience.
    if (this.authoredAudio) {
      this.stopBattleOpening();
      this.battleCueWasSelected = false;
      this.selectNextPlaylistTrack();
      return;
    }
    if (!this.battleCueWasSelected) {
      this.battleCueWasSelected = true;
      this.playBattleOpening();
      return;
    }
    this.battleCueWasSelected = false;
    this.selectNextPlaylistTrack();
  }

  private playAuthoredTrack(track: AuthoredTrackDef, autoAdvance: boolean): void {
    if (!this.enabled || this.externalPlayback || typeof Audio === 'undefined') return;
    this.stopBattleOpening();
    this.battleOpeningResumeName = this.currentSongName;
    const player = new Audio(track.path);
    player.preload = 'auto';
    player.volume = this.volume * 0.72;
    player.addEventListener('ended', () => {
      if (this.authoredAudio !== player) return;
      this.authoredAudio = null;
      this.battleOpeningActive = false;
      this.battleCueWasSelected = false;
      this.setExternalPlaybackActive(false);
      if (autoAdvance || !this.menuMode) {
        this.selectNextPlaylistTrack(true);
      } else {
        this.currentSongName = this.battleOpeningResumeName;
        this.step = 0;
        this.nextTime = this.ctx ? this.ctx.currentTime + 0.05 : 0;
      }
    });
    this.authoredAudio = player;
    this.currentSongName = track.name;
    this.battleOpeningActive = true;
    this.setExternalPlaybackActive(true);
    void player.play().catch(() => {
      if (this.authoredAudio !== player) return;
      this.authoredAudio = null;
      this.battleOpeningActive = false;
      this.currentSongName = this.battleOpeningResumeName;
      this.setExternalPlaybackActive(false);
    });
  }

  private selectNextPlaylistTrack(autoAdvance = false): void {
    if (!this.started) this.unlock();
    const entry = this.nextFromPlaylistBag();
    this.currentPlaylistEntry = entry;
    if (entry.kind === 'audio') {
      this.playAuthoredTrack(PLAYLIST_AUDIO[entry.index], true);
      return;
    }
    this.songIndex = entry.index;
    this.step = 0;
    this.nextTime = this.ctx ? this.ctx.currentTime + 0.05 : 0;
    this.currentSongName = SONGS[this.songIndex].name;
    if (autoAdvance) this.setExternalPlaybackActive(false);
  }

  /** Duck the procedural score while a commander-selected local track plays. */
  setExternalPlaybackActive(active: boolean): void {
    this.externalPlayback = active;
    this.applyMaster(0.2);
  }

  private applyMaster(ramp: number): void {
    if (this.master && this.ctx) {
      const t = this.ctx.currentTime;
      const goal = !this.enabled || this.externalPlayback || this.volume <= 0
        ? 0
        : (this.menuMode ? 0.24 : 0.4) * this.volume;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setValueAtTime(this.master.gain.value, t);
      this.master.gain.linearRampToValueAtTime(goal, t + ramp);
    }
  }

  // ---- scheduler ----

  private schedule(): void {
    const ctx = this.ctx;
    if (!ctx || !this.started || !this.enabled) return;
    if (ctx.state !== 'running') {
      this.nextTime = ctx.currentTime + 0.1;
      return;
    }
    if (this.externalPlayback) {
      // Keep the scheduler clock parked while the MP3 playlist item owns the
      // output. This prevents a hidden synth track from consuming a minute
      // of progress before it becomes audible.
      this.nextTime = ctx.currentTime + 0.1;
      return;
    }
    this.intensity += (this.target - this.intensity) * 0.05;

    while (this.nextTime < ctx.currentTime + 0.12) {
      const song = SONGS[this.songIndex];
      const stepDur = 60 / song.bpm / 4;
      this.playStep(this.step, this.nextTime, song);
      this.step++;
      if (this.step >= song.bars * 16) {
        this.step = 0;
        this.selectNextPlaylistTrack(true);
      }
      this.nextTime += stepDur;
    }
  }

  private nextFromPlaylistBag(): PlaylistEntry {
    if (!this.menuMode && !this.authoredShuffle && PLAYLIST_AUDIO.length) {
      const next = this.currentPlaylistEntry.kind === 'audio'
        ? (this.currentPlaylistEntry.index + 1) % PLAYLIST_AUDIO.length
        : 0;
      return { kind: 'audio', index: next };
    }
    if (this.playlistBag.length === 0) {
      const currentKey = this.currentPlaylistEntry.kind === 'audio'
        ? `audio:${this.currentPlaylistEntry.index}`
        : `synth:${this.currentPlaylistEntry.index}`;
      const entries: PlaylistEntry[] = this.menuMode
        ? SONGS.map((_, index) => ({ kind: 'synth' as const, index }))
        : PLAYLIST_AUDIO.map((_, index) => ({ kind: 'audio' as const, index }));
      this.playlistBag = entries.filter((entry) => {
        if (entry.kind === 'synth') return currentKey !== `synth:${entry.index}`;
        return currentKey !== `audio:${entry.index}`;
      });
      for (let i = this.playlistBag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.playlistBag[i], this.playlistBag[j]] = [this.playlistBag[j], this.playlistBag[i]];
      }
    }
    return this.playlistBag.pop() ?? { kind: 'synth', index: 0 };
  }

  private playStep(step: number, t: number, song: SongDef): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const chord = song.prog[bar % song.prog.length];
    const drums = DRUMS[song.style];
    // Always keep an audible floor so the menu still has a real groove.
    const energy = Math.max(this.intensity, this.menuMode ? 0.4 : 0.55);

    if (drums.kick.includes(s)) this.kick(t, 0.95);
    if (drums.snare.includes(s)) this.snare(t, 0.45 + energy * 0.3);
    if (drums.hat.includes(s)) this.hat(t, 0.1 + energy * 0.12);

    // Martial snare fill at the end of every 4th bar.
    if (song.style === 'march' && bar % 4 === 3 && s >= 12) this.snare(t, 0.22);

    if (BASS_STEPS[song.style].includes(s)) {
      const dur = (60 / song.bpm) / 2;
      this.bass(t, midi(song.root - 12 + chord[0]), dur, 0.5 + energy * 0.35);
    }

    if (song.style === 'march' && (s === 0 || s === 8)) {
      this.stab(t, chord.map((o) => midi(song.root + o)), 0.5, 0.16 + energy * 0.14);
    } else if (song.style === 'dark' && s === 0) {
      this.stab(t, chord.map((o) => midi(song.root + o)), 1.3, 0.14 + energy * 0.12);
    } else if (song.style === 'edm' || song.style === 'rush') {
      const arpSteps = song.style === 'rush' ? [0, 2, 4, 6, 8, 10, 12, 14] : [0, 3, 6, 8, 11, 14];
      if (energy > 0.42 && arpSteps.includes(s)) {
        const idx = arpSteps.indexOf(s);
        const tone = chord[idx % chord.length] + (idx >= chord.length ? 12 : 0);
        this.lead(t, midi(song.root + 12 + tone), (60 / song.bpm) / 4 * 0.9, 0.11 + energy * 0.1);
      }
    }

    // High counter-melody once the battle heats up.
    if (energy > 0.7 && song.style === 'march' && (s === 0 || s === 6)) {
      this.lead(t, midi(song.root + 24 + chord[0]), 0.3, 0.08);
    }
  }

  // ---- instruments ----

  private kick(t: number, amp: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(165, t);
    o.frequency.exponentialRampToValueAtTime(46, t + 0.12);
    g.gain.setValueAtTime(amp * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    o.connect(g).connect(this.bus!);
    o.start(t);
    o.stop(t + 0.24);
  }

  private snare(t: number, amp: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1900;
    bp.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(amp * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.17);
    src.connect(bp).connect(g).connect(this.bus!);
    src.start(t);
    src.stop(t + 0.2);

    const o = ctx.createOscillator();
    const og = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = 185;
    og.gain.setValueAtTime(amp * 0.22, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
    o.connect(og).connect(this.bus!);
    o.start(t);
    o.stop(t + 0.13);
  }

  private hat(t: number, amp: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 7200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(amp * 0.4, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    src.connect(hp).connect(g).connect(this.bus!);
    src.start(t);
    src.stop(t + 0.06);
  }

  private bass(t: number, freq: number, dur: number, amp: number): void {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 420 + this.intensity * 900;
    lp.Q.value = 6;
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(amp * 0.26, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    lp.connect(g).connect(this.bus!);
    for (const [type, det] of [['sawtooth', 0], ['square', -8]] as [OscillatorType, number][]) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      o.detune.value = det;
      o.connect(lp);
      o.start(t);
      o.stop(t + dur + 0.02);
    }
  }

  private stab(t: number, freqs: number[], dur: number, amp: number): void {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1100 + this.intensity * 2400;
    lp.Q.value = 2;
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(amp * 0.15, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    lp.connect(g).connect(this.bus!);
    for (const f of freqs) {
      for (const det of [-7, 7]) {
        const o = ctx.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = f;
        o.detune.value = det;
        o.connect(lp);
        o.start(t);
        o.stop(t + dur + 0.02);
      }
    }
  }

  private lead(t: number, freq: number, dur: number, amp: number): void {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2800;
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(amp * 0.11, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    lp.connect(g).connect(this.bus!);
    for (const det of [-9, 9]) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = freq;
      o.detune.value = det;
      o.connect(lp);
      o.start(t);
      o.stop(t + dur + 0.02);
    }
  }

  stinger(kind: 'wave' | 'boss' | 'ability' | 'breach'): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const play = (freq: number, dur: number, type: OscillatorType, amp: number, delay: number) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq;
      const st = t + delay;
      g.gain.setValueAtTime(0.001, st);
      g.gain.linearRampToValueAtTime(amp, st + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, st + dur);
      o.connect(g).connect(this.master!);
      o.start(st);
      o.stop(st + dur + 0.02);
    };
    if (kind === 'wave') {
      play(220, 0.15, 'square', 0.12, 0);
      play(330, 0.2, 'square', 0.1, 0.1);
    } else if (kind === 'boss') {
      play(80, 0.5, 'sawtooth', 0.16, 0);
      play(60, 0.6, 'sawtooth', 0.14, 0.15);
      play(45, 0.8, 'sawtooth', 0.12, 0.3);
    } else if (kind === 'ability') {
      play(400, 0.08, 'sine', 0.1, 0);
      play(600, 0.12, 'sine', 0.1, 0.05);
      play(900, 0.18, 'sine', 0.1, 0.1);
    } else {
      play(180, 0.25, 'sawtooth', 0.14, 0);
      play(120, 0.35, 'sawtooth', 0.12, 0.05);
    }
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.started = false;
  }
}

export const music = new MusicSystem();
